/**
 * Formats a date as an RFC 3339 timestamp with an explicit local timezone offset,
 * e.g. `2026-07-24T13:24:25.481+02:00`.
 *
 * Unlike `Date.prototype.toISOString`, which always emits UTC (`Z`), this preserves
 * the local timezone so the receiver knows the caller's actual offset.
 *
 * @param date - The date to format. Defaults to the current date/time.
 * @returns The RFC 3339 formatted string with a numeric timezone offset.
 */
export const toRfc3339WithOffset = (date: Date = new Date()): string => {
    const pad = (value: number, length = 2): string => Math.abs(value).toString().padStart(length, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const milliseconds = pad(date.getMilliseconds(), 3);

    // getTimezoneOffset returns minutes behind UTC (positive when local is behind UTC),
    // so the sign is inverted relative to the RFC 3339 offset.
    const offsetMinutes = date.getTimezoneOffset();
    const sign = offsetMinutes > 0 ? '-' : '+';
    const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
    const offsetMins = pad(Math.abs(offsetMinutes) % 60);

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${sign}${offsetHours}:${offsetMins}`;
};
